import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import AnnouncementModal from "./AnnouncementModal";

export const Logo = () => (
  <Link to="/" className="logo" data-testid="brand-home">
    <img src="/brand/wha-logo.png" alt="World Help Agency" className="logo-mark" /> WORLD HELP AGENCY
  </Link>
);

const links = [
  { label: "Packages", to: "/packages" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Referral Program", to: "/referral-program" },
  { label: "FAQ", to: "/faq" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export function PublicNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="topbar">
      <Logo />
      <nav className={open ? "mobile-open" : ""}>
        {links.map((l) => (
          <Link
            key={l.label}
            to={l.to}
            data-testid={`nav-${l.label.toLowerCase().replaceAll(" ", "-")}`}
            onClick={() => setOpen(false)}
          >
            {l.label}
          </Link>
        ))}
        <Link data-testid="nav-login" to="/login" className="nav-login" onClick={() => setOpen(false)}>
          Login <ArrowRight size={15} />
        </Link>
      </nav>
      <button className="icon-btn mobile-menu" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>
        {open ? <X /> : <Menu />}
      </button>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer>
      <Logo />
      <span>© 2026 World Help Agency. All rights reserved.</span>
      <div>
        <Link data-testid="footer-terms" to="/terms">Terms &amp; Conditions</Link>
        <Link data-testid="footer-privacy" to="/privacy">Privacy Policy</Link>
        <Link data-testid="footer-risk" to="/risk-disclosure">Risk Disclosure</Link>
        <Link data-testid="footer-aml" to="/aml-policy">AML Policy</Link>
        <Link data-testid="footer-contact" to="/contact">Contact</Link>
      </div>
    </footer>
  );
}

export function PublicShell({ children }) {
  return (
    <div className="site-shell">
      <PublicNav />
      {children}
      <PublicFooter />
    </div>
  );
}

export function MemberLayout({ user, active, children }) {
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = [
    ["Dashboard", "/dashboard", "⌂"],
    ["GAMES", "/dashboard/games", "♠"],
    ["Active Packages", "/dashboard/packages", "◈"],
    ["Production", "/dashboard/production", "◒"],
    ["Packages", "/dashboard/deposits", "↓"],
    ["Withdrawals", "/dashboard/withdrawals", "↑"],
    ["Referrals", "/dashboard/referrals", "◎"],
    ["Referral Tree", "/dashboard/referral-tree", "⌘"],
    ["Transactions", "/dashboard/transactions", "≡"],
    ["Wallet", "/dashboard/wallet", "▣"],
    ["Profile", "/dashboard/profile", "○"],
    ["Security", "/dashboard/security", "◇"],
  ];
  async function logout() {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("ha_user");
    nav("/");
  }
  return (
    <div className={`member-app ${active === "Dashboard" || active === "GAMES" ? "member-dashboard-dark" : ""} ${drawerOpen ? "drawer-open" : ""}`}>
      <button className="drawer-trigger" data-testid="member-drawer-trigger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
        <Menu size={18} />
      </button>
      {drawerOpen && <div className="drawer-backdrop" data-testid="member-drawer-backdrop" onClick={() => setDrawerOpen(false)} />}
      <aside className="sidebar" data-testid="member-sidebar">
        <div className="sidebar-head">
          <Logo />
          <button className="drawer-close" data-testid="member-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>
        <div className="side-label">MEMBER SPACE</div>
        {items.map(([label, to, icon]) => (
          <Link
            key={label}
            to={to}
            onClick={() => setDrawerOpen(false)}
            className={active === label ? "active" : ""}
            data-testid={`member-nav-${label.toLowerCase().replaceAll(" ", "-")}`}
          >
            <span className="nav-icon">{icon}</span>
            <span className="nav-label">{label}</span>
          </Link>
        ))}
        <button data-testid="member-logout-button" className="side-logout" onClick={logout}>
          <span className="nav-icon">↗</span><span className="nav-label">Logout</span>
        </button>
      </aside>
      <main className="member-main">
        <div className="member-top">
          <div>
            <div className="eyebrow">MEMBER / {active?.toUpperCase()}</div>
            <h1>Hello, {user?.full_name?.split(" ")[0] || "member"}.</h1>
          </div>
          <div className="member-avatar" data-testid="member-avatar">
            {(user?.full_name || "M")[0]}
          </div>
        </div>
        {children}
      </main>
      <AnnouncementModal />
    </div>
  );
}

export function AdminLayout({ user, active, children }) {
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = [
    ["Yönetim Paneli", "/admin"],
    ["Kullanıcılar", "/admin/kullanicilar"],
    ["Paketler", "/admin/paketler"],
    ["Depozitolar", "/admin/depozitolar"],
    ["Çekimler", "/admin/cekimler"],
    ["Referans Sistemi", "/admin/referans-ayarlari"],
    ["Referans Ağacı", "/admin/referans-agaci"],
    ["Üretimler", "/admin/uretimler"],
    ["İşlem Defteri", "/admin/islem-defteri"],
    ["Cüzdan Ayarları", "/admin/cuzdan-ayarlari"],
    ["Site Ayarları", "/admin/site-ayarlari"],
    ["Oyun Ayarları", "/admin/oyun-ayarlari"],
    ["Bildirimler", "/admin/bildirimler"],
    ["Yönetici Hesapları", "/admin/yonetici-hesaplari"],
    ["Güvenlik", "/admin/guvenlik"],
    ["Sistem Logları", "/admin/sistem-loglari"],
  ];
  async function logout() {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("ha_user");
    nav("/");
  }
  return (
    <div className={`admin-app ${drawerOpen ? "drawer-open" : ""}`}>
      <button className="drawer-trigger" data-testid="admin-drawer-trigger" onClick={() => setDrawerOpen(true)} aria-label="Menüyü aç">
        <Menu size={18} />
      </button>
      {drawerOpen && <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />}
      <aside className="admin-side" data-testid="admin-sidebar">
        <div className="sidebar-head">
          <Logo />
          <button className="drawer-close" data-testid="admin-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Kapat">
            <X size={16} />
          </button>
        </div>
        <div className="admin-badge">YÖNETİM MERKEZİ</div>
        {items.map(([label, to]) => (
          <Link
            key={label}
            to={to}
            onClick={() => setDrawerOpen(false)}
            className={active === label ? "active" : ""}
            data-testid={`admin-nav-${label.toLowerCase().replaceAll(" ", "-").replace(/ğ/g,"g").replace(/ü/g,"u").replace(/ı/g,"i").replace(/ö/g,"o").replace(/ç/g,"c").replace(/ş/g,"s")}`}
          >
            <span className="nav-label">{label}</span>
          </Link>
        ))}
        <button data-testid="admin-logout-button" className="side-logout" onClick={logout}>
          <span className="nav-label">↗ Çıkış</span>
        </button>
      </aside>
      <main className="admin-main">
        <div className="admin-top">
          <div>
            <div className="eyebrow">YÖNETİM / {active?.toUpperCase()}</div>
            <h1>{active}</h1>
          </div>
          <div className="admin-user">
            {user?.email || "admin@example.com"}
            <span>● Çevrimiçi</span>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}
